import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ControlContainer, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { AdminService } from '../../admin/admin.service';
import { Provider } from '@models/mood/general/provider';

export interface ProviderForm {
  provider_id: FormControl<string | null>;
  source_provider: FormControl<boolean | null>;
}

export interface DatasetProviderEntry {
  provider_id: string;
  source_provider?: boolean;
}

@Component({
  selector: 'mood-admin-provider-selection-form',
  templateUrl: './provider-selection-form.component.html',
  styleUrls: [
    'provider-selection-form.component.scss'
  ]
})
export class ProviderSelectionFormComponent implements OnInit {
  @Output() removeProviderEntryTriggered = new EventEmitter();

  providerList: Provider[] = [];

  constructor(private _controlContainer: ControlContainer, private _adminService: AdminService) { }

  get formGroupContainer() {
    return this._controlContainer.control as FormGroup<ProviderForm>;
  }

  public static createProviderFormGroup(existingProvider?: DatasetProviderEntry) {
    const formGroup = new FormGroup<ProviderForm>({
      provider_id: new FormControl<string | null>(existingProvider?.provider_id || null, [Validators.required]),
      source_provider: new FormControl<boolean | null>(existingProvider?.source_provider || null)
    });

    return formGroup;
  }

  providerControl = new FormControl<string | Provider>('', Validators.required);
  filteredProviderList?: Observable<Provider[]>;

  async ngOnInit() {
    this.providerList  = await this._adminService.getProviders();

    this.filteredProviderList = this.providerControl.valueChanges.pipe(
      debounceTime(400),
      startWith(''),
      distinctUntilChanged(),
      map(value => {
        const name = typeof value === 'string' ? value : value!.name;
        return this._filterProviders(name);
      })
    );

    if (this.formGroupContainer.get('provider_id')?.value) {
      const existingProvider = await this.providerList.find(provider => provider.id === this.formGroupContainer.get('provider_id')!.value!);
      this.providerControl.patchValue(existingProvider!);
    }
  }

  private _filterProviders(value: string): Provider[] {
    const filterValue = value.toLowerCase();

    return this.providerList.filter(provider => provider.name.toLowerCase().includes(filterValue));
  }

  selectProvider(event: MatAutocompleteSelectedEvent) {
    let selectedProvider = event.option.value as Provider;

    this.formGroupContainer.patchValue({
      provider_id: selectedProvider.id
    });
    this.formGroupContainer.markAsDirty();
  }

  displayProvider(provider: Provider): string {
    return provider?.name;
  }

  removeProviderEntry() {
    this.removeProviderEntryTriggered.emit();
  }
}
